// External Libraries
import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

// Services
import BankReconciliationService from "../../services/BankReconciliationService";

// Common Components
import WComponent from "../common/WComponent";
import Loader from "../common/loader/Loader";

// Styles
import "./reconciliations-overview.css";

const MONTHS = [
	{ value: 1, label: "January" },
	{ value: 2, label: "February" },
	{ value: 3, label: "March" },
	{ value: 4, label: "April" },
	{ value: 5, label: "May" },
	{ value: 6, label: "June" },
	{ value: 7, label: "July" },
	{ value: 8, label: "August" },
	{ value: 9, label: "September" },
	{ value: 10, label: "October" },
	{ value: 11, label: "November" },
	{ value: 12, label: "December" }
];

class ReconciliationsOverview extends WComponent {
	constructor(props) {
		super(props);

		const previousMonth = moment().subtract(1, "month");

		this.state = {
			selectedMonth: previousMonth.month() + 1,
			selectedYear: previousMonth.year(),
			loading: true,
			clientGroups: []
		};
	}

	componentDidMount() {
		this.fetchData();
	}

	fetchData = async () => {
		let { selectedMonth, selectedYear } = this.state;

		await this.update({ loading: true });

		const reconciliations = await BankReconciliationService.listAllByMonth({
			year: selectedYear,
			month: selectedMonth
		});

		// Group reconciliations by client_id
		const grouped = {};
		reconciliations.forEach(recon => {
			const clientId = recon.client_id;
			if (!grouped[clientId]) {
				grouped[clientId] = {
					clientId,
					clientName: recon.client?.name || `Client ${clientId}`,
					reconciliations: [],
					expanded: false
				};
			}
			grouped[clientId].reconciliations.push(recon);
		});

		// Compute status + fetch data quality alert count for each client (in parallel)
		const clientGroups = await Promise.all(
			Object.values(grouped).map(async group => {
				const hasIssue = group.reconciliations.some(
					r => r.needs_review || parseFloat(r.difference) !== 0
				);
				let dqAlertCount = 0;
				try {
					const alerts = await BankReconciliationService.fetchDataQualityAlerts({ clientId: group.clientId });
					dqAlertCount = (alerts || []).filter(a => a.status !== "resolved").length;
				} catch (e) {
					// swallow — DQ is supplementary
				}
				return {
					...group,
					status: hasIssue ? "needs_review" : "balanced",
					dqAlertCount
				};
			})
		);

		// Sort by client name
		clientGroups.sort((a, b) => a.clientName.localeCompare(b.clientName));

		await this.update({ clientGroups, loading: false });
	};

	onMonthChange = async e => {
		await this.update({ selectedMonth: parseInt(e.target.value) });
		this.fetchData();
	};

	onYearChange = async e => {
		await this.update({ selectedYear: parseInt(e.target.value) });
		this.fetchData();
	};

	toggleClient = async clientId => {
		let { clientGroups } = this.state;
		const updated = clientGroups.map(g =>
			g.clientId === clientId ? { ...g, expanded: !g.expanded } : g
		);
		await this.update({ clientGroups: updated });
	};

	onReconciliationClick = (clientId, reconciliationId) => {
		// Navigate to client tools page
		this.props.history.push(`/clients/corporate/${clientId}`);
	};

	formatCurrency(amount) {
		const num = parseFloat(amount) || 0;
		const formatted = Math.abs(num).toLocaleString("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		});
		return num < 0 ? `-$${formatted}` : `$${formatted}`;
	}

	renderStatusBadge(group) {
		if (group.status === "balanced") {
			return <span className="recon-overview__badge recon-overview__badge--balanced">✓ Balanced</span>;
		}
		return <span className="recon-overview__badge recon-overview__badge--needs-review">! Needs Review</span>;
	}

	renderReconciliationRow(recon, clientId) {
		return (
			<div
				key={recon.id}
				className="recon-overview__recon-row"
				onClick={() => this.onReconciliationClick(clientId, recon.id)}
			>
				<div className="recon-overview__recon-info">
					<span className="recon-overview__recon-title">
						{recon.account_name || "Bank Account"} — {recon.period_start} to {recon.period_end}
					</span>
					<span className="recon-overview__recon-meta">
						Difference: {this.formatCurrency(recon.difference)} | Created:{" "}
						{moment(recon.created_at).format("MMM D, YYYY h:mm A")}
					</span>
				</div>
				<div className="recon-overview__recon-badges">
					{recon.is_auto && (
						<span className="recon-overview__small-badge recon-overview__small-badge--auto">AUTO</span>
					)}
					{recon.needs_review && (
						<span className="recon-overview__small-badge recon-overview__small-badge--review">
							NEEDS REVIEW
						</span>
					)}
					<span
						className={`recon-overview__small-badge recon-overview__small-badge--${recon.status}`}
					>
						{recon.status}
					</span>
				</div>
			</div>
		);
	}

	renderClientAccordion(group) {
		return (
			<div key={group.clientId} className="recon-overview__client">
				<div className="recon-overview__client-header" onClick={() => this.toggleClient(group.clientId)}>
					<div className="recon-overview__client-header-left">
						{group.expanded ? <Icon.ChevronDown size={18} /> : <Icon.ChevronRight size={18} />}
						<span className="recon-overview__client-name">{group.clientName}</span>
					</div>
					<div className="recon-overview__client-header-right">
						{this.renderStatusBadge(group)}
						{group.dqAlertCount > 0 && (
							<span className="recon-overview__badge recon-overview__badge--dq-issues">
								⚠ {group.dqAlertCount} DQ Issue{group.dqAlertCount !== 1 ? "s" : ""}
							</span>
						)}
						<span className="recon-overview__recon-count">
							{group.reconciliations.length} {group.reconciliations.length === 1 ? "recon" : "recons"}
						</span>
					</div>
				</div>
				{group.expanded && (
					<div className="recon-overview__client-body">
						{group.reconciliations.map(r => this.renderReconciliationRow(r, group.clientId))}
					</div>
				)}
			</div>
		);
	}

	render() {
		let { selectedMonth, selectedYear, loading, clientGroups } = this.state;

		const currentYear = moment().year();
		const years = [];
		for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

		return (
			<div className="container">
				<div className="container-header-title">
					<h1 className="container-header-title__name">Reconciliations</h1>
				</div>

				<div className="recon-overview__body">
					<div className="recon-overview__filters">
						<div className="recon-overview__filter">
							<label>Month</label>
							<select value={selectedMonth} onChange={this.onMonthChange}>
								{MONTHS.map(m => (
									<option key={m.value} value={m.value}>
										{m.label}
									</option>
								))}
							</select>
						</div>
						<div className="recon-overview__filter">
							<label>Year</label>
							<select value={selectedYear} onChange={this.onYearChange}>
								{years.map(y => (
									<option key={y} value={y}>
										{y}
									</option>
								))}
							</select>
						</div>
					</div>

					{loading && (
						<div className="container-loader">
							<Loader />
						</div>
					)}

					{!loading && clientGroups.length === 0 && (
						<div className="recon-overview__empty">
							No reconciliations found for {MONTHS[selectedMonth - 1].label} {selectedYear}.
						</div>
					)}

					{!loading && clientGroups.length > 0 && (
						<div className="recon-overview__list">
							{clientGroups.map(group => this.renderClientAccordion(group))}
						</div>
					)}
				</div>
			</div>
		);
	}
}

export default ReconciliationsOverview;
